import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Dialog, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { BarChart, Money, Provenance, Rate } from '@/components/money';
import { useOperation, useService, type ServiceDetail } from '@/api/hooks';
import { formatDate, formatDateTime, formatMoney, formatNumber, formatRelative } from '@/lib/format';
import { rateSourceLabel, recordSourceLabel } from './wallets/walletView';
import { useIsMobile } from '@/lib/useMediaQuery';
import { BILLING_LABELS, PROVIDER_LABELS, forecastText } from './FinanceScreen';
import { CredentialDialog } from './services/CredentialDialog';
import { ErrorState, Loading } from '@/components/QueryState';
import { ApiError } from '@/api/client';
import {
  ManualUsageDialog,
  ReconcileDialog,
  ServiceFormDialog,
  ServiceTopUpDialog,
  SubscriptionDialog,
} from './services/ServiceDialogs';

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <Card padding={isMobile ? 'sm' : 'md'}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-5)',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
          }}
        >
          {title}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function BalanceBlock({ service }: { service: ServiceDetail }) {
  if (service.billingModel === 'subscription') {
    return (
      <div>
        {service.subscription ? (
          <>
            <Money minor={service.subscription.amount} currency={service.currency} size="xl" />
            <Provenance>
              {`Списание ${formatDate(service.subscription.nextChargeAt)}, ` +
                (service.subscription.period === 'monthly' ? 'раз в месяц' : 'раз в год')}
            </Provenance>
            {service.subscription.seats ? (
              <Provenance>{`Мест: ${service.subscription.seats}`}</Provenance>
            ) : null}
          </>
        ) : (
          <Provenance>Параметры подписки ещё не заданы</Provenance>
        )}
      </div>
    );
  }

  if (service.billingModel === 'postpaid_invoice') {
    return (
      <div>
        <Money minor={service.providerPendingMinor} currency={service.currency} size="xl" />
        <Provenance>Набежало за текущий месяц, цифра пришла от провайдера</Provenance>
        {service.providerBalanceMinor !== null ? (
          <Provenance>
            {`Остаток на счету ${formatMoney(service.providerBalanceMinor, service.currency)}`}
          </Provenance>
        ) : null}
        <Provenance>
          {service.providerBalanceAt
            ? `Обновлено ${formatRelative(service.providerBalanceAt)}`
            : 'Данные ещё не собирались'}
        </Provenance>
      </div>
    );
  }

  const negative = (service.balanceMinor ?? 0) < 0;
  return (
    <div>
      <Money
        minor={service.balanceMinor}
        currency={service.currency}
        size="xl"
        tone={negative ? 'danger' : 'normal'}
      />
      <Provenance>
        Расчётный баланс: пополнения минус расход плюс корректировки
        {negative ? '. Отрицательный — требуется сверка' : ''}
      </Provenance>
      <Provenance>
        {service.lastReconciledAt
          ? `Последняя сверка ${formatDate(service.lastReconciledAt)}`
          : 'Не сверялся ни разу'}
      </Provenance>
      <Provenance>
        {service.usageThroughDate
          ? `Расход подтянут по ${formatDate(service.usageThroughDate)}`
          : 'Расход ещё не вносился'}
      </Provenance>
      {negative ? null : (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Provenance>{forecastText(service)}</Provenance>
        </div>
      )}
    </div>
  );
}

export function ServiceDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const service = useService(id);

  const [toppingUp, setToppingUp] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [addingUsage, setAddingUsage] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(false);
  const [deletingTopUp, setDeletingTopUp] = useState<string | null>(null);
  const [deletingUsage, setDeletingUsage] = useState<string | null>(null);
  const [editingCredential, setEditingCredential] = useState(false);

  const removeTopUp = useOperation<{ id: string }>('service_topup.delete');
  const removeUsage = useOperation<{ id: string }>('usage.delete');
  const runSync = useOperation<
    { serviceId: string },
    {
      results: Array<{
        status: 'ok' | 'failed' | 'skipped';
        usageRecords: number;
        error: string | null;
        reason?: string;
      }>;
    }
  >('sync.run');
  const [syncMessage, setSyncMessage] = useState<{ failed: boolean; text: string } | null>(null);

  /**
   * Результат ручной синхронизации показывается всегда. Раньше кнопка молча
   * проглатывала и отказ провайдера, и паузу между запусками: человек жал
   * снова и не понимал, что происходит.
   */
  function syncNow(serviceId: string) {
    setSyncMessage(null);
    runSync.mutate(
      { serviceId },
      {
        onSuccess: (data) => {
          const result = data.results[0];
          if (!result) return;
          if (result.status === 'ok') {
            setSyncMessage({
              failed: false,
              text: `Данные обновлены, записей расхода: ${result.usageRecords}`,
            });
          } else if (result.status === 'skipped') {
            setSyncMessage({ failed: true, text: `Пропущено: ${result.reason ?? 'причина не указана'}` });
          } else {
            setSyncMessage({ failed: true, text: `Не удалось: ${result.error ?? 'причина не указана'}` });
          }
        },
        onError: (error) => {
          setSyncMessage({
            failed: true,
            text: error instanceof ApiError ? error.message : 'Синхронизация не удалась',
          });
        },
      },
    );
  }

  if (service.isLoading) {
    return (
      <div className="kj-page">
        <Loading what="сервис" />
      </div>
    );
  }

  // Сетевой сбой и удалённый сервис — разные вещи, и говорить о них надо разное.
  const missing =
    service.error instanceof ApiError &&
    (service.error.status === 404 || service.error.status === 400);

  if (service.isError && !missing) {
    return (
      <div className="kj-page">
        <ErrorState
          what="сервис"
          error={service.error}
          onRetry={() => void service.refetch()}
          retrying={service.isFetching}
        />
      </div>
    );
  }

  if (service.isError || !service.data) {
    return (
      <div className="kj-page">
        <EmptyState
          icon="Wallet"
          title="Сервис не найден"
          description="Возможно, он был удалён. Вернитесь к списку и выберите другой."
          action={
            <Button variant="secondary" onClick={() => navigate('/finance')}>
              К списку сервисов
            </Button>
          }
        />
      </div>
    );
  }

  const data = service.data;
  const isPrepaid = data.billingModel === 'prepaid_balance';

  return (
    <div className="kj-page">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/finance')}
          iconLeft={<Icon name="ArrowLeft" size={16} />}
        >
          Все сервисы
        </Button>
      </div>

      <Card padding="lg">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 'var(--space-7)',
          }}
        >
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  font: '500 24px var(--font-display)',
                  letterSpacing: 'var(--ls-heading)',
                  color: 'var(--text-strong)',
                }}
              >
                {data.name}
              </span>
              {data.isActive ? null : <Badge tone="neutral">Отключён</Badge>}
            </div>
            <div
              style={{
                marginTop: 'var(--space-3)',
                font: '400 13px var(--font-text)',
                color: 'var(--text-subtle)',
              }}
            >
              {(PROVIDER_LABELS[data.provider] ?? data.provider) +
                ' · ' +
                (BILLING_LABELS[data.billingModel] ?? data.billingModel) +
                ' · ' +
                data.currency}
            </div>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Provenance>
                {data.syncStatus === 'never'
                  ? 'Данные вносятся вручную'
                  : data.lastSyncAt
                    ? `Синхронизация ${formatRelative(data.lastSyncAt)}`
                    : 'Синхронизация ещё не запускалась'}
              </Provenance>
              {data.syncStatus === 'failed' && data.lastSyncError ? (
                <span
                  style={{
                    display: 'block',
                    font: '400 12px/1.45 var(--font-text)',
                    color: 'var(--status-danger-fg)',
                    maxWidth: 420,
                  }}
                >
                  {data.lastSyncError}
                </span>
              ) : null}
              {data.credential ? (
                <Provenance>
                  {`Ключ ${data.credential.maskedHint}` +
                    (data.credential.isValid === false ? ', отвергнут провайдером' : '')}
                </Provenance>
              ) : null}
              {data.defaultWalletName ? (
                <Provenance>{`Кошелёк по умолчанию: ${data.defaultWalletName}`}</Provenance>
              ) : null}
              {data.averageServiceRate !== null ? (
                <Provenance>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    Средний курс пополнений <Rate rate={data.averageServiceRate} /> за 1{' '}
                    {data.currency}
                  </span>
                </Provenance>
              ) : null}
            </div>
          </div>

          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <BalanceBlock service={data} />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            <Button onClick={() => setToppingUp(true)} iconLeft={<Icon name="Plus" size={16} />}>
              Пополнить
            </Button>
            {isPrepaid ? (
              <Button variant="secondary" onClick={() => setReconciling(true)}>
                Сверить баланс
              </Button>
            ) : null}
            {data.balanceUrl ? (
              <Button
                variant="secondary"
                as="a"
                href={data.balanceUrl}
                iconRight={<Icon name="ExternalLink" size={16} />}
              >
                Баланс в кабинете
              </Button>
            ) : null}
            {data.billingModel === 'subscription' ? (
              <Button variant="secondary" onClick={() => setEditingSubscription(true)}>
                Параметры подписки
              </Button>
            ) : null}
            {data.needsCredential ? (
              <Button
                variant="secondary"
                onClick={() => setEditingCredential(true)}
                iconLeft={<Icon name="KeyRound" size={16} />}
              >
                {data.credential ? 'Ключ доступа' : 'Задать ключ'}
              </Button>
            ) : null}
            {data.supportsSync && data.credential ? (
              <Button
                variant="ghost"
                loading={runSync.isPending}
                onClick={() => syncNow(data.id)}
                iconLeft={<Icon name="RefreshCw" size={16} />}
              >
                Синхронизировать
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Изменить
            </Button>
          </div>
        </div>

        {syncMessage ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 'var(--space-4)',
              font: '400 13px/1.5 var(--font-text)',
              color: syncMessage.failed ? 'var(--status-danger-fg)' : 'var(--text-muted)',
            }}
          >
            {syncMessage.text}
          </div>
        ) : null}
      </Card>

      <SectionCard
        title="Расход по дням"
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAddingUsage(true)}
            iconLeft={<Icon name="Plus" size={16} />}
          >
            Внести расход
          </Button>
        }
      >
        <BarChart
          bars={data.usageByDay.map((day) => ({
            label: day.date.slice(8, 10) + '.' + day.date.slice(5, 7),
            value: day.amount,
            caption: formatDate(day.date),
          }))}
          currency={data.currency}
          emptyMessage="За последний месяц расхода не было"
        />
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Provenance>Последние 30 дней. Выделен последний день с расходом</Provenance>
        </div>
      </SectionCard>

      {data.byModel.length > 0 ? (
        <SectionCard title="Потребление по моделям">
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Модель</th>
                  <th>Расход</th>
                  <th>Входные</th>
                  <th>Выходные</th>
                  <th>Кэш</th>
                  <th>Запросы</th>
                </tr>
              </thead>
              <tbody>
                {data.byModel.map((row) => (
                  <tr key={row.model}>
                    <td data-label="Модель" style={{ color: 'var(--text-strong)' }}>{row.model}</td>
                    <td data-label="Расход">
                      <Money minor={row.amount} currency={data.currency} size="sm" />
                    </td>
                    <td data-label="Входные" className="kj-mono">
                      {row.inputTokens > 0 ? formatNumber(row.inputTokens) : '—'}
                    </td>
                    <td data-label="Выходные" className="kj-mono">
                      {row.outputTokens > 0 ? formatNumber(row.outputTokens) : '—'}
                    </td>
                    <td data-label="Кэш" className="kj-mono">
                      {row.cachedTokens > 0 ? formatNumber(row.cachedTokens) : '—'}
                    </td>
                    <td data-label="Запросы" className="kj-mono">
                      {row.requestCount > 0 ? formatNumber(row.requestCount) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Provenance>Последние 30 дней, разрезы приходят от провайдера</Provenance>
          </div>
        </SectionCard>
      ) : null}

      {data.invoices.length > 0 ? (
        <SectionCard title="Счета">
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Период</th>
                  <th>Сумма</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td data-label="Период" className="kj-mono">{formatDate(invoice.periodStart)}</td>
                    <td data-label="Сумма">
                      <Money minor={invoice.amount} currency={data.currency} size="sm" />
                    </td>
                    <td data-label="Статус" style={{ color: 'var(--text-muted)' }}>
                      {invoice.status === 'paid'
                        ? 'оплачен'
                        : invoice.status === 'open'
                          ? 'к оплате'
                          : 'неизвестно'}
                    </td>
                    <td data-label="" style={{ textAlign: 'right' }}>
                      {invoice.url ? (
                        <a href={invoice.url} target="_blank" rel="noreferrer">
                          Открыть у провайдера
                        </a>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard title="Записи расхода">
        {data.usage.length === 0 ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
            Расход ещё не вносился.
          </div>
        ) : (
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>День</th>
                  <th>Сумма</th>
                  <th>Разрез</th>
                  <th>Источник</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.usage.slice(0, 60).map((row) => (
                  <tr key={row.id}>
                    <td data-label="День" className="kj-mono">{formatDate(row.date)}</td>
                    <td data-label="Сумма">
                      <Money minor={row.amount} currency={data.currency} size="sm" />
                    </td>
                    <td data-label="Разрез" style={{ color: 'var(--text-muted)' }}>{row.note ?? '—'}</td>
                    <td data-label="Источник" style={{ color: 'var(--text-subtle)' }}>
                      {row.source === 'manual' ? 'вручную' : 'провайдер'}
                    </td>
                    <td data-label="" style={{ textAlign: 'right' }}>
                      {row.source === 'manual' ? (
                        <Button variant="ghost" size="sm" onClick={() => setDeletingUsage(row.id)}>
                          Удалить
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title={isPrepaid ? 'История пополнений' : 'Оплаты'}>
        {data.topUps.length === 0 ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
            {isPrepaid ? 'Пополнений ещё не было.' : 'Оплат ещё не было.'}
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {data.topUps.map((row) => (
              <Card key={row.id} tone="sunken" padding="sm" elevation="none">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Money minor={row.amount} currency={data.currency} />
                  <span className="kj-mono" style={{ fontSize: 'var(--fs-2xs)' }}>
                    {formatDate(row.occurredAt)}
                  </span>
                </div>
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <Provenance>
                    {row.walletName ? `С кошелька «${row.walletName}»` : 'Кошелёк не указан'}
                    {row.fee > 0 ? ` · комиссия ${formatMoney(row.fee, data.currency)}` : ''}
                    {row.rubCost !== null
                      ? ` · себестоимость ${formatMoney(row.rubCost, 'RUB')}, ${rateSourceLabel(row.rateSource)}`
                      : ' · курс не найден'}
                  </Provenance>
                  <Provenance>
                    {`Внесено ${recordSourceLabel(row.source)}`}
                    {row.author ? `, ${row.author}` : ''}
                  </Provenance>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Сумма</th>
                  <th>Кошелёк</th>
                  <th>Себестоимость</th>
                  <th>Курс</th>
                  <th>Откуда</th>
                  <th>Кто</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.topUps.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Дата" className="kj-mono">{formatDate(row.occurredAt)}</td>
                    <td data-label="Сумма">
                      <Money minor={row.amount} currency={data.currency} size="sm" />
                      {row.fee > 0 ? (
                        <Provenance>{`комиссия ${formatMoney(row.fee, data.currency)}`}</Provenance>
                      ) : null}
                    </td>
                    <td data-label="Кошелёк" style={{ color: 'var(--text-muted)' }}>{row.walletName ?? '—'}</td>
                    <td data-label="Себестоимость">
                      <Money minor={row.rubCost} currency="RUB" size="sm" tone="muted" />
                    </td>
                    <td data-label="Курс" style={{ color: 'var(--text-subtle)' }}>
                      {row.rubCost === null ? (
                        'не найден'
                      ) : (
                        <>
                          {rateSourceLabel(row.rateSource)}
                          {row.rateE4 !== null ? (
                            <Provenance>
                              {`${formatNumber(row.rateE4 / 10_000, 2)} ₽`}
                              {row.rateAt && (row.rateSource === 'p2p' || row.rateSource === 'cbr')
                                ? `, ${formatDateTime(row.rateAt)}`
                                : ''}
                            </Provenance>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td data-label="Откуда" style={{ color: 'var(--text-muted)' }}>
                      {recordSourceLabel(row.source)}
                    </td>
                    <td data-label="Кто">{row.author ?? '—'}</td>
                    <td data-label="" style={{ textAlign: 'right' }}>
                      <Button variant="ghost" size="sm" onClick={() => setDeletingTopUp(row.id)}>
                        Удалить
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {isPrepaid ? (
        <SectionCard title="История сверок">
          {data.adjustments.length === 0 ? (
            <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
              Сверок ещё не было. Раз в неделю стоит сравнить расчётный остаток с кабинетом сервиса.
            </div>
          ) : (
            <div className="kj-scroll-x">
              <table className="kj-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Было расчётно</th>
                    <th>Стало фактически</th>
                    <th>Разница</th>
                    <th>Кто</th>
                  </tr>
                </thead>
                <tbody>
                  {data.adjustments.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Дата" className="kj-mono">{formatDate(row.occurredAt)}</td>
                      <td data-label="Было расчётно">
                        <Money
                          minor={row.calculatedBalance}
                          currency={data.currency}
                          size="sm"
                          tone="muted"
                        />
                      </td>
                      <td data-label="Стало фактически">
                        <Money minor={row.actualBalance} currency={data.currency} size="sm" />
                      </td>
                      <td data-label="Разница">
                        <Money
                          minor={row.delta}
                          currency={data.currency}
                          size="sm"
                          tone={
                            Math.abs(row.delta) > Math.abs(row.calculatedBalance) * 0.1
                              ? 'warning'
                              : 'normal'
                          }
                        />
                      </td>
                      <td data-label="Кто">{row.author ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : null}

      <ServiceTopUpDialog
        service={data}
        open={toppingUp}
        onClose={() => setToppingUp(false)}
      />
      <ReconcileDialog service={data} open={reconciling} onClose={() => setReconciling(false)} />
      <ManualUsageDialog service={data} open={addingUsage} onClose={() => setAddingUsage(false)} />
      <ServiceFormDialog service={data} open={editing} onClose={() => setEditing(false)} />
      <CredentialDialog
        service={data}
        open={editingCredential}
        onClose={() => setEditingCredential(false)}
      />
      <SubscriptionDialog
        service={data}
        open={editingSubscription}
        onClose={() => setEditingSubscription(false)}
      />

      <Dialog
        open={deletingTopUp !== null}
        title="Удалить пополнение"
        description="Баланс и прогноз пересчитаются, остаток кошелька вернётся. Запись останется в журнале действий."
        onClose={() => setDeletingTopUp(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeletingTopUp(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={removeTopUp.isPending}
              onClick={() => {
                if (!deletingTopUp) return;
                removeTopUp.mutate({ id: deletingTopUp }, { onSuccess: () => setDeletingTopUp(null) });
              }}
            >
              Удалить
            </Button>
          </>
        }
      />

      <Dialog
        open={deletingUsage !== null}
        title="Удалить запись расхода"
        description="Баланс и прогноз пересчитаются. Запись останется в журнале действий."
        onClose={() => setDeletingUsage(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeletingUsage(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={removeUsage.isPending}
              onClick={() => {
                if (!deletingUsage) return;
                removeUsage.mutate({ id: deletingUsage }, { onSuccess: () => setDeletingUsage(null) });
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </div>
  );
}
